import { SearchContextType, useSearch } from "../search-context";
import { SearchFieldErrors, SearchFieldError } from "../types";
import validateAddressSearch from "./address-validator";
import validateDrivingLicenseSearch from "./driving-license-validator";
import validateEmailSearch from "./email-validator";
import ValidationErrorMessages from "./error-messages";
import validateNISearch from "./ni-validator";
import validateOrgSearch from "./organisation-validator";
import validatePassportSearch from "./passport-validator";
import validateAddressPersonSearch from "./person-address-validator";
import validatePersonSearch from "./person-validator";
import validatePhoneSearch from "./phone-validator";
import { Validator } from "./types";
import validateVehicleSearch from "./vehicle-validator";

const validateOptionalFields = (data: SearchContextType): boolean => {
  return (
    data.optionalSearchValues?.some((key) => {
      const value = data[key as keyof typeof data];
      return typeof value === "string" && value.trim().length > 0;
    }) ?? false
  );
};

const checkClaimReference = (
  searchContext: SearchContextType,
): SearchFieldError[] | null => {
  if (!searchContext.claimReference.trim()) {
    return [
      {
        title: "Claim Reference Search",
        error: ValidationErrorMessages.claimReferenceRequired,
      },
    ];
  }
  return null;
};

const checkOptionalFields = (
  searchContext: SearchContextType,
): SearchFieldError[] | null => {
  if (!validateOptionalFields(searchContext)) {
    return [
      {
        title: "Optional Fields Search",
        error: ValidationErrorMessages.optionalFieldsRequired,
      },
    ];
  }
  return null;
};

const useValidateFormData = () => {
  const searchContext = useSearch();

  const validators: { name: string; validator: Validator }[] = [
    { name: "Address Search", validator: validateAddressSearch },
    { name: "Person Search", validator: validatePersonSearch },
    { name: "Driving License Search", validator: validateDrivingLicenseSearch },
    { name: "Vehicle Search", validator: validateVehicleSearch },
    { name: "NI Search", validator: validateNISearch },
    { name: "Email Search", validator: validateEmailSearch },
    { name: "Phone Search", validator: validatePhoneSearch },
    { name: "Passport Search", validator: validatePassportSearch },
    { name: "Organisation Search", validator: validateOrgSearch },
    { name: "Address Person Search", validator: validateAddressPersonSearch },
  ];

  const validate = (): {
    errorsState: SearchFieldErrors;
    errors: SearchFieldError[];
  } => {
    const errors: SearchFieldError[] = [];
    const errorsState: SearchFieldErrors = {
      claimRefErr: false,
      requiredFieldErr: false,
    };

    const claimReferenceErrors = checkClaimReference(searchContext);
    if (claimReferenceErrors) {
      errorsState.claimRefErr = true;
      errors.push(...claimReferenceErrors);
      return { errorsState, errors };
    }

    const optionalFieldsErrors = checkOptionalFields(searchContext);
    if (optionalFieldsErrors) {
      errorsState.requiredFieldErr = true;
      errors.push(...optionalFieldsErrors);
      return { errorsState, errors };
    }

    let isValidSearch = false;

    validators.forEach(({ validator, name }) => {
      const result = validator(searchContext);
      isValidSearch = isValidSearch || result.isValid;

      if (!result.isValid && result.errors.length > 0) {
        result.errors.forEach((error) => {
          errors.push({ title: name, error });
        });
      }
    });

    if (isValidSearch) {
      return { errorsState, errors: [] };
    }

    return { errorsState, errors };
  };

  return validate;
};

export default useValidateFormData;
